import React, { useState, useEffect, useCallback } from "react";
import { getOBTManagementInfoById, saveOBTInfo } from "services/userServices";
import { useNavigate, useLocation } from "react-router-dom";
import SnackBar from "components/SnackBar/SnackBar";
import BackdropComponent from "components/Spinner/Spinner";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridFooterContainer,
  GridPagination,
  getGridSingleSelectOperators,
} from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { styled } from "@mui/material/styles";
import CustomBulkEditHeader from "./CustomBulkEditHeader";
import { format } from "date-fns";
import PropTypes from "prop-types";
import DialogBox from "components/ConfirmationDialog/DialogBox";
import moment from "moment";
import PeoplePicker from "components/Common/PeoplePicker/PeoplePicker";
import { getPortfolioList } from "services/learnPointUserServices";

// ─── L&D field config ─────────────────────────────────────────────────────────
const LD_PEOPLE_FIELDS = [
  {
    field: "ldExecutiveName",
    oracleField: "ldExecutiveOracleId",
    label: "LD Executive",
  },
  {
    field: "ldLevel1Name",
    oracleField: "ldLevel1OracleId",
    label: "LD Level 1",
  },
  {
    field: "ldLevel2Name",
    oracleField: "ldLevel2OracleId",
    label: "LD Level 2",
  },
  {
    field: "ldLevel3Name",
    oracleField: "ldLevel3OracleId",
    label: "LD Level 3",
  },
  {
    field: "ldLevel4Name",
    oracleField: "ldLevel4OracleId",
    label: "LD Level 4",
  },
];

const PORTFOLIO_FALLBACK = [
  { id: 1, name: "Auto Core" },
  { id: 2, name: "Auto Percepta" },
  { id: 3, name: "BFSI" },
  { id: 4, name: "Healthcare" },
  { id: 5, name: "Public Sector" },
  { id: 6, name: "TMC" },
  { id: 7, name: "Travel & Retail" },
];

// ─── Styled components ────────────────────────────────────────────────────────
const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": { backgroundColor: "#2878CC" },
}));
const CustomOutlinedButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  borderColor: "#3299FF",
  color: "#3299FF",
  "&:hover": { borderColor: "#3299FF", backgroundColor: "#E0F2FF" },
}));
const StyledDataGrid = styled(DataGrid)(({ theme }) => {
  const f = {
    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
    fontSize: "12px",
    letterSpacing: "0.10em",
  };
  return {
    "& .MuiDataGrid-root": { ...f },
    "& .MuiDataGrid-columnsContainer": {
      backgroundColor: theme.palette.background.paper,
      ...f,
    },
    "& .MuiDataGrid-cell": {
      ...f,
      boxSizing: "border-box",
      width: "100%",
      maxWidth: "100%",
      overflow: "hidden",
      borderBottom: `1px solid ${theme.palette.divider}`,
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: theme.palette.background.default,
      boxSizing: "border-box",
    },
    "& .MuiDataGrid-columnHeader": {
      ...f,
      width: "100%",
      maxWidth: "100%",
      overflow: "hidden",
      backgroundColor: theme.palette.background.default,
      lineHeight: "100px",
      borderBottom: `1px solid ${theme.palette.divider}`,
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    "& .MuiDataGrid-columnHeaderTitle": { ...f, fontWeight: "bold" },
    "& .MuiDataGrid-row": {
      backgroundColor: theme.palette.background.default,
      ...f,
      "&:hover": { backgroundColor: theme.palette.action.hover },
    },
    "& .MuiDataGrid-virtualScroller": { overflowX: "auto" },
    "& .unsaved": { backgroundColor: "#ffecb3", color: "#000" },
    "& .saved": {
      animation: "fadeToTransparent 5s forwards",
      backgroundColor: "#d4edda",
      color: "#155724",
    },
    "@keyframes fadeToTransparent": {
      "0%": { backgroundColor: "#d4edda", color: "#155724" },
      "100%": { backgroundColor: "transparent", color: "inherit" },
    },
  };
});
const CustomFooter = ({ pageSize, rowCount, editableCellsCount }) => (
  <GridFooterContainer>
    <Box
      sx={{
        flex: "1 1 auto",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Typography
        sx={{
          paddingLeft: 1,
          fontFamily: "Urbanist, Verdana, Arial, sans-serif",
          fontSize: "12px",
          letterSpacing: "0.10em",
        }}
      >
        Total records:&nbsp;{rowCount}
      </Typography>
      {editableCellsCount > 0 && (
        <Typography
          sx={{
            fontFamily: "Urbanist, Verdana, Arial, sans-serif",
            fontSize: "12px",
            letterSpacing: "0.10em",
            paddingLeft: 1,
            textAlign: "center",
            flex: 1,
          }}
        >
          Total unsaved cells: {editableCellsCount}
        </Typography>
      )}
    </Box>
    {rowCount > pageSize && (
      <GridPagination labelRowsPerPage="Rows per page:" />
    )}
  </GridFooterContainer>
);
CustomFooter.propTypes = {
  pageSize: PropTypes.number.isRequired,
  rowCount: PropTypes.number.isRequired,
  editableCellsCount: PropTypes.number.isRequired,
};

const VISIBLE_EDITABLE_FIELDS = new Set([
  // IDM / OG fields
  "idmSupport",
  "ogSupported",
  "idmDeploymentStatus",
  "rampDownDate",
  "rosterLockBusDays",
  "rosterLockFrom",
  "ttecAccessSLABusDays",
  "clientAccessSLABusDays",
  "clientAccessRequestType",
  "primaryIDMPOC",
  "backupIDMPOC",
  "clientDistro",
  "clientOffboarding",
  "idmTeamLead",
  "idmTeamEmail",
  "primaryOGs",
  "backupOGs",
  "ogOwner",
  "ogEscalationPoint",
  "ogTeamEmail",
  "idmOwner",
  "idmEscalationPoint",
  "switchIDPOC",
  // TA — highlight is on the OracleId key (see cellClassName), name is visual only
  "primaryTAOracleId",
  "backupTAOracleId",
  "taTeamEmail",
  // L&D — same pattern: highlight driven by OracleId key
  "ldExecutiveOracleId",
  "ldLevel1OracleId",
  "ldLevel2OracleId",
  "ldLevel3OracleId",
  "ldLevel4OracleId",
  // Portfolio — highlight driven by lP_PortfolioId key
  "lP_PortfolioId",
  // Boolean requirement fields
  "contactNumber",
  "gender",
  "nationalIdentifier",
  "dateOfBirth",
  "country",
  "recruiter",
  "token",
  "confirmPreviousEmploymentWithClient",
  "nickname",
  "licenseNPN",
  "productTrainingDate",
  "address",
  "city",
  "state",
  "postalCode",
  "availability",
  "inboundOutbound",
  "language",
  "integrationDate",
  "email",
  "nestingDate",
  "productionDate",
  "backgroundStatusDate",
  "personalEmail",
]);

function PeoplePickerEditCell({
  params,
  nameField,
  oracleField,
  defaultVal,
  rows,
  markEdited,
}) {
  // Initialised once from defaultVal — subsequent renders with a stale
  // defaultVal do NOT overwrite this because there is no useEffect on it.
  const [selectedEmployee, setSelectedEmployee] = useState(defaultVal ?? null);

  const handleChange = (employee) => {
    if (!employee) return;

    // 1. Update local state immediately → re-renders this cell with new name
    setSelectedEmployee(employee);

    // 2. Commit the display name into DataGrid's cell value
    params.api.setEditCellValue({
      id: params.id,
      field: nameField,
      value: employee.Name,
    });

    // 3. Write the Oracle ID directly onto the row object so processRowUpdate
    //    and the save payload both pick it up correctly
    const row = rows.find((r) => r.id === params.id);
    if (row) row[oracleField] = employee.OracleId;

    // 4. Mark both fields dirty for the unsaved-cell highlight
    markEdited([params.id], [nameField, oracleField]);
  };

  const handleReset = () => {
    setSelectedEmployee(null);
    params.api.setEditCellValue({ id: params.id, field: nameField, value: "" });
    const row = rows.find((r) => r.id === params.id);
    if (row) row[oracleField] = null;
    markEdited([params.id], [nameField, oracleField]);
  };

  return (
    <Box sx={{ width: "100%", px: 0.5 }}>
      {/* Immediate visual confirmation of the current selection */}
      {selectedEmployee?.Name && (
        <Typography
          sx={{
            fontSize: "11px",
            color: "primary.main",
            fontWeight: 600,
            lineHeight: 1.2,
            mb: 0.25,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          ✓ {selectedEmployee.Name}
        </Typography>
      )}
      <PeoplePicker
        label=""
        placeholder="Search employee"
        // Pass the resolved object so PeoplePicker shows the current name
        // without firing a search API call
        defaultValue={selectedEmployee}
        onChange={handleChange}
        onReset={handleReset}
      />
    </Box>
  );
}

PeoplePickerEditCell.propTypes = {
  params: PropTypes.object.isRequired,
  nameField: PropTypes.string.isRequired,
  oracleField: PropTypes.string.isRequired,
  defaultVal: PropTypes.object,
  rows: PropTypes.array.isRequired,
  markEdited: PropTypes.func.isRequired,
};

function PortfolioBulkEditHeader({
  portfolioOptions,
  selectedRows,
  onBulkEdit,
  setSnack,
  resetValue,
}) {
  const [value, setValue] = useState("");

  // Reset the header dropdown whenever selectedRows becomes empty
  // (same trigger pattern used by CustomBulkEditHeader's resetValue prop)
  useEffect(() => {
    setValue("");
  }, [resetValue]);

  const handleChange = (e) => {
    const portfolioName = e.target.value;
    setValue(portfolioName);

    if (!selectedRows.length) {
      setSnack({
        open: true,
        message: "No rows selected for bulk edit.",
        severity: "error",
      });
      return;
    }

    onBulkEdit("portfolioName", portfolioName);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        gap: 0.5,
        py: 0.5,
      }}
    >
      <Typography
        sx={{
          fontFamily: "Urbanist, Verdana, Arial, sans-serif",
          fontSize: "12px",
          fontWeight: "bold",
          letterSpacing: "0.10em",
        }}
      >
        Portfolio
      </Typography>
      <Select
        size="small"
        displayEmpty
        value={value}
        onChange={handleChange}
        disabled={!selectedRows.length}
        // Prevent DataGrid from treating the click as a column-sort trigger
        onClick={(e) => e.stopPropagation()}
        // disablePortal keeps the Menu in the same DOM subtree as the header,
        // avoiding the "contains on Node" error that portal-rendered Menus
        // cause inside DataGrid column headers
        MenuProps={{
          disablePortal: true,
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          transformOrigin: { vertical: "top", horizontal: "left" },
        }}
        sx={{
          width: "100%",
          fontSize: "11px",
          "& .MuiSelect-select": { py: "2px", px: "6px" },
        }}
      >
        <MenuItem value="" disabled>
          <em>Bulk set…</em>
        </MenuItem>
        {portfolioOptions.map((o) => (
          <MenuItem key={o.id} value={o.name} sx={{ fontSize: "11px" }}>
            {o.name}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

PortfolioBulkEditHeader.propTypes = {
  portfolioOptions: PropTypes.array.isRequired,
  selectedRows: PropTypes.array.isRequired,
  onBulkEdit: PropTypes.func.isRequired,
  setSnack: PropTypes.func.isRequired,
  resetValue: PropTypes.any,
};

// ─── Main component ───────────────────────────────────────────────────────────
function BulkEditIDMOGOwnerShip() {
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const snackReset = () => setSnack({ open: false, message: "", severity: "" });

  const location = useLocation();
  const navigate = useNavigate();

  const [selectedParentClient, setSelectedParentClient] = useState("");
  const [rows, setRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]); // always DataGrid row.id (number)
  const [filteredRowCount, setFilteredRowCount] = useState(0);
  const [savedCells, setSavedCells] = useState({});
  const [editedCells, setEditedCells] = useState({});
  const [gridPageSize, setGridPageSize] = useState(10);
  const [shiftPressed, setShiftPressed] = useState(false);
  const [totalUnsavedCells, setTotalUnsavedCells] = useState(0);
  const [headerResetTrigger, setHeaderResetTrigger] = useState(false);
  const [filterModel, setFilterModel] = useState({ items: [] });

  // Dynamic portfolio
  const [portfolioOptions, setPortfolioOptions] = useState(PORTFOLIO_FALLBACK);
  const [portfolioLoading, setPortfolioLoading] = useState(false);

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState(null);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogMessageType, setDialogMessageType] = useState("Confirmation");
  const [dialogBulletList, setDialogBulletList] = useState([]);
  const [isConfirmationDialog, setIsConfirmationDialog] = useState(true);

  // ── Fetch portfolio list on mount ──────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPortfolioLoading(true);
      try {
        const res = await getPortfolioList();
        if (!cancelled && res.status && res.data) {
          setPortfolioOptions(
            res.data.map((p) => ({
              id: p.lP_PortfolioId || p.LP_PortfolioId,
              name: p.portfolioName || p.PortfolioName,
            })),
          );
        }
      } catch {
        /* keep fallback */
      } finally {
        if (!cancelled) setPortfolioLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDialogOpen = (
    action,
    title,
    message,
    messageType,
    bulletList,
    isConfirmation,
  ) => {
    setDialogAction(() => action);
    setDialogTitle(title);
    setDialogMessage(message);
    setDialogMessageType(messageType);
    setDialogBulletList(bulletList);
    setIsConfirmationDialog(isConfirmation);
    setDialogOpen(true);
  };

  const calculatePageSize = () => {
    const h = window.innerHeight;
    if (h > 1200) return 100;
    if (h > 900) return 50;
    if (h > 600) return 25;
    return 10;
  };

  // ── Fetch row data ─────────────────────────────────────────────────────────
  const handleFetchData = useCallback(async () => {
    if (!location.state) return;
    setLoading(true);
    try {
      setSelectedParentClient(location.state.parentClient);
      const response = await getOBTManagementInfoById(location.state.OBTIds);
      const dataWithIds = response.data.map((item, index) => ({
        ...item,
        id: item.obtManagementInfoId,
        rowIndex: index + 1,
        // TA
        primaryTAOracleId:
          item.primaryTAOracleId || item.PrimaryTAOracleId || null,
        backupTAOracleId:
          item.backupTAOracleId || item.BackupTAOracleId || null,
        taTeamEmail: item.taTeamEmail || item.TATeamEmail || "",
        // L&D oracle IDs
        ldExecutiveOracleId:
          item.ldExecutiveOracleId || item.LDExecutiveOracleId || null,
        ldLevel1OracleId:
          item.ldLevel1OracleId || item.LDLevel1OracleId || null,
        ldLevel2OracleId:
          item.ldLevel2OracleId || item.LDLevel2OracleId || null,
        ldLevel3OracleId:
          item.ldLevel3OracleId || item.LDLevel3OracleId || null,
        ldLevel4OracleId:
          item.ldLevel4OracleId || item.LDLevel4OracleId || null,
        // Portfolio — store under the key the API expects
        lP_PortfolioId: Number(
          item.lP_PortfolioId || item.LP_PortfolioId || item.lpPortfolioId || 4,
        ),
        portfolioName: item.portfolioName || item.PortfolioName || "Healthcare",
      }));

      setRows(dataWithIds);
      setFilteredRowCount(dataWithIds.length);
      setEditedCells({});
      setSavedCells({});
      setSelectedRows([]);
      setFilterModel({ items: [] });
      setHeaderResetTrigger(true);
      const updatePageSize = () => setGridPageSize(calculatePageSize());
      updatePageSize();
      window.addEventListener("resize", updatePageSize);
    } catch {
      setSnack({
        open: true,
        message: "An error occurred while fetching data.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [location.state]);

  useEffect(() => {
    let isMounted = true; // guard — flipped to false in cleanup before observer fires
    let rafId = null; // requestAnimationFrame handle for debounce
    let observer = null;

    // Defer setup until after the first paint so .MuiDataGrid-root exists
    const initRaf = requestAnimationFrame(() => {
      if (!isMounted) return; // navigated away before first paint

      observer = new ResizeObserver(() => {
        // Debounce: cancel any pending frame and schedule a new one.
        // This collapses bursts of resize notifications into a single callback,
        // which is what prevents the "loop with undelivered notifications" error.
        if (rafId) cancelAnimationFrame(rafId);

        rafId = requestAnimationFrame(() => {
          // Guard: if the component unmounted while this frame was queued,
          // do nothing — the element is already gone from the DOM.
          if (!isMounted) return;

          const el = document.querySelector(".MuiDataGrid-root");
          if (el) el.style.width = `${el.offsetWidth}px`;
        });
      });

      const el = document.querySelector(".MuiDataGrid-root");
      if (el) observer.observe(el);
    });

    // Shift key tracking
    const onDown = (e) => {
      if (e.key === "Shift") setShiftPressed(true);
    };
    const onUp = (e) => {
      if (e.key === "Shift") setShiftPressed(false);
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);

    // Initial data fetch
    handleFetchData();

    return () => {
      // Flip the guard FIRST — any already-queued RAF callbacks become no-ops
      isMounted = false;

      // Cancel the init frame if navigation happened before first paint
      cancelAnimationFrame(initRaf);

      // Cancel any pending debounce frame
      if (rafId) cancelAnimationFrame(rafId);

      // Disconnect the observer — safe to call even if observe() was never called
      if (observer) observer.disconnect();

      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
    };
  }, [handleFetchData]);

  useEffect(() => {
    const count = Object.values(editedCells).reduce((sum, cellMap) => {
      // Only count keys that correspond to a visible editable column.
      // Paired "shadow" keys like primaryTAName, ldDirectorName, portfolioName
      // are skipped — their paired OracleId / lP_PortfolioId key is what
      // drives the cellClassName highlight and is already in the set.
      const visibleCount = Object.keys(cellMap).filter((f) =>
        VISIBLE_EDITABLE_FIELDS.has(f),
      ).length;
      return sum + visibleCount;
    }, 0);

    setTotalUnsavedCells(count);
  }, [editedCells]);

  useEffect(() => {
    setRows((p) => [...p]);
  }, [savedCells, editedCells]);

  useEffect(() => {
    if (selectedRows.length === 0) setHeaderResetTrigger((p) => !p);
  }, [selectedRows]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const calculateColumnWidth = (
    rows,
    field,
    headerLabel = "",
    minWidth = 80,
  ) => {
    // Characters-to-pixels constants tuned for Urbanist 12px
    const CHAR_PX = 8.5; // average glyph width at 12px
    const PADDING = 24; // left + right cell padding

    // Widest data value in the column
    const maxDataLen = rows.length
      ? Math.max(
          ...rows.map((r) =>
            r[field] != null ? r[field].toString().length : 0,
          ),
        )
      : 0;

    // Header label length (fall back to field name if no label supplied)
    const headerLen = (headerLabel || field).length;

    const contentWidth = Math.max(maxDataLen, headerLen) * CHAR_PX + PADDING;
    return Math.max(Math.ceil(contentWidth), minWidth);
  };
  const getFilterOptions = (field) => {
    const unique = [...new Set(rows.map((r) => r[field] || ""))];
    return unique.map((v) => ({ label: v, value: v }));
  };
  const validateEmail = (field, value) => {
    if (field !== "clientDistro" && field !== "taTeamEmail") {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }
    if (!value) return true;
    return value
      .split(";")
      .every(
        (e) =>
          e.trim().length === 0 ||
          /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/i.test(
            e.trim(),
          ),
      );
  };

  // ── markEdited helper ──────────────────────────────────────────────────────
  const markEdited = (ids, fields) => {
    setEditedCells((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        if (!next[id]) next[id] = {};
        fields.forEach((f) => {
          next[id][f] = true;
        });
      });
      return next;
    });
  };

  // ── Bulk-edit handlers ─────────────────────────────────────────────────────
  const handleBulkEdit = (field, value) => {
    if (!selectedRows.length) {
      setSnack({
        open: true,
        message: "No rows selected for bulk edit.",
        severity: "error",
      });
      return;
    }
    setRows((prev) =>
      prev.map((r) =>
        selectedRows.includes(r.id) ? { ...r, [field]: value } : r,
      ),
    );
    markEdited(selectedRows, [field]);
  };

  // Unified people-picker bulk edit — TA + all L&D fields
  const handleBulkEditPeoplePicker = (field, employee) => {
    if (!selectedRows.length) {
      setSnack({
        open: true,
        message: "No rows selected for bulk edit.",
        severity: "error",
      });
      return;
    }
    if (!employee?.OracleId) {
      setSnack({
        open: true,
        message: "Please select a valid employee.",
        severity: "error",
      });
      return;
    }

    const oracleFieldMap = {
      primaryTAName: "primaryTAOracleId",
      backupTAName: "backupTAOracleId",
      ldExecutiveName: "ldExecutiveOracleId",
      ldLevel1Name: "ldLevel1OracleId",
      ldLevel2Name: "ldLevel2OracleId",
      ldLevel3Name: "ldLevel3OracleId",
      ldLevel4Name: "ldLevel4OracleId",
    };
    const oracleField = oracleFieldMap[field];

    setRows((prev) =>
      prev.map((r) =>
        selectedRows.includes(r.id)
          ? {
              ...r,
              [field]: employee.Name,
              ...(oracleField ? { [oracleField]: employee.OracleId } : {}),
            }
          : r,
      ),
    );
    markEdited(selectedRows, oracleField ? [field, oracleField] : [field]);
  };

  // Portfolio bulk edit — receives portfolio NAME string from CustomBulkEditHeader
  const handleBulkEditPortfolio = (field, portfolioName) => {
    if (!selectedRows.length) {
      setSnack({
        open: true,
        message: "No rows selected for bulk edit.",
        severity: "error",
      });
      return;
    }
    const portfolio = portfolioOptions.find((p) => p.name === portfolioName);
    if (!portfolio) {
      setSnack({
        open: true,
        message: "Invalid portfolio selection.",
        severity: "error",
      });
      return;
    }

    const portfolioId = Number(portfolio.id);

    setRows((prev) =>
      prev.map((r) =>
        selectedRows.includes(r.id)
          ? { ...r, [field]: portfolio.name, lP_PortfolioId: portfolioId }
          : r,
      ),
    );

    markEdited(selectedRows, [field, "lP_PortfolioId"]);
  };

  const handleProcessRowUpdate = (newRow, oldRow) => {
    const changedFields = Object.keys(newRow).filter(
      (f) => newRow[f] !== oldRow[f],
    );
    const emailFields = columns
      .filter(
        (c) =>
          c.type === "string" &&
          (c.field.toLowerCase().includes("email") ||
            c.field.toLowerCase().includes("distro")),
      )
      .map((c) => c.field);
    const invalidEmails = changedFields
      .filter((f) => emailFields.includes(f))
      .filter((f) => newRow[f] && !validateEmail(f, newRow[f]));
    if (invalidEmails.length) {
      setSnack({
        open: true,
        message: `Invalid email(s) in field(s): ${invalidEmails.join(", ")}, please correct and update again.`,
        severity: "error",
      });
      return oldRow;
    }
    setRows((prev) => prev.map((r) => (r.id === newRow.id ? newRow : r)));
    markEdited([newRow.id], changedFields);
    return newRow;
  };

  const handleSaveData = async () => {
    if (!totalUnsavedCells) {
      setSnack({
        open: true,
        message: "No unsaved changes detected.",
        severity: "info",
      });
      return;
    }

    handleDialogOpen(
      async () => {
        setLoading(true);
        try {
          const editedRowIds = Object.keys(editedCells).map(Number);
          const rowsToSave = rows.filter((r) => editedRowIds.includes(r.id));

          const response = await saveOBTInfo(rowsToSave);

          if (response?.message === "Success") {
            setSavedCells({ ...editedCells });
            setTimeout(() => {
              setSavedCells({});
              setEditedCells({});
            }, 5000);
            setSnack({
              open: true,
              message: "All changes have been saved successfully.",
              severity: "success",
            });
          } else {
            setSnack({
              open: true,
              message: "Failed to Save",
              severity: "error",
            });
          }
        } catch {
          setSnack({
            open: true,
            message: "Failed to Save",
            severity: "error",
          });
        } finally {
          setLoading(false);
        }
      },
      "Save Data",
      "Are you sure you want to save the bulk changes for IDM OG Ownership?",
      "Confirmation",
      [],
      true,
    );
  };
  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
    const filtered = rows.filter((row) =>
      newFilterModel.items.every(({ columnField, value, operatorValue }) => {
        if (operatorValue === "is")
          return row[columnField]?.toString() === value;
        return true;
      }),
    );
    setFilteredRowCount(filtered.length);
    if (filtered.length > 0 && selectedRows.length > 0)
      setSelectedRows(filtered.map((r) => r.id));
  };

  const handleCancelButton = () => {
    handleDialogOpen(
      async () =>
        navigate("/obtinfo/accountOwnership", {
          state: { parentClient: selectedParentClient?.parentClientDesc },
        }),
      "Cancel Bulk Edit",
      totalUnsavedCells > 0
        ? "You may lose the unsaved changes, Are you sure you want to cancel?"
        : "Are you sure you want to cancel?",
      totalUnsavedCells > 0 ? "Warning" : "Confirmation",
      [],
      true,
    );
  };

  const handleSelectionModelChange = (newSelection) => {
    if (newSelection.length > 50) {
      setSnack({
        open: true,
        message: "You can only select up to 50 records for bulk edit.",
        severity: "warning",
      });
      setSelectedRows(newSelection.slice(0, 50));
    } else {
      setSelectedRows(newSelection);
    }
  };

  const resolvedEmployeeFromRow = (row, nameField, oracleField) => {
    if (!row?.[oracleField]) return null;
    return {
      OracleId: row[oracleField],
      Name: row[nameField] || "",
      Display: row[nameField] || "",
      Email: "",
    };
  };

  // Reusable inline PeoplePicker edit cell factory
  const makePeoplePickerEditCell = (nameField, oracleField) => (params) => {
    const currentRow = rows.find((r) => r.id === params.id);

    // resolvedEmployeeFromRow is only used here to get the INITIAL value.
    // After mount, PeoplePickerEditCell owns its own state and ignores
    // any subsequent defaultVal passed from outside.
    const defaultVal = resolvedEmployeeFromRow(
      currentRow,
      nameField,
      oracleField,
    );

    return (
      <PeoplePickerEditCell
        // key on oracleField+id ensures a fresh mount (and fresh useState)
        // when the user opens a different row's edit cell
        key={`${params.id}-${oracleField}`}
        params={params}
        nameField={nameField}
        oracleField={oracleField}
        defaultVal={defaultVal}
        rows={rows}
        markEdited={markEdited}
      />
    );
  };

  // ── Column definitions ─────────────────────────────────────────────────────
  const columns = [
    // Read-only identifier columns
    {
      field: "rowIndex",
      headerName: "Row #",
      type: "number",
      hideable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      textAlign: "right",
      headerAlign: "center",
      width: calculateColumnWidth(rows, "rowIndex", "Row #", 70),
      renderHeader: () => (
        <Typography
          sx={{
            fontFamily: "Urbanist, Verdana, Arial, sans-serif",
            fontSize: "12px",
            letterSpacing: "0.10em",
            fontWeight: "bold",
          }}
        >
          Row #
        </Typography>
      ),
    },
    ...[
      "parentClientDesc",
      "clientDesc",
      "programDesc",
      "projectDesc",
      "projectCodeFull",
    ].map((field) => {
      const labels = {
        parentClientDesc: "Parent Client",
        clientDesc: "Client",
        programDesc: "Program",
        projectDesc: "Project",
        projectCodeFull: "Full Project Code",
      };
      return {
        field,
        headerName: labels[field],
        type: "singleSelect",
        headerAlign: "center",
        hideable: false,
        sortable: true,
        filterable: true,
        width: calculateColumnWidth(rows, field, labels[field], 100),
        renderHeader: () => (
          <Tooltip title={labels[field]} placement="top">
            <Typography
              sx={{
                fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                fontSize: "12px",
                letterSpacing: "0.10em",
                fontWeight: "bold",
              }}
            >
              {labels[field]}
            </Typography>
          </Tooltip>
        ),
        valueOptions: getFilterOptions(field).map((o) => o.label),
        filterOperators: getGridSingleSelectOperators().filter(
          (op) => op.value === "is",
        ),
      };
    }),

    // IDM Support
    {
      field: "idmSupport",
      headerName: "IDM Support",
      type: "singleSelect",
      hideable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      editable: true,
      headerAlign: "center",
      width: calculateColumnWidth(rows, "idmSupport", "IDM Support", 130),
      renderHeader: () => (
        <CustomBulkEditHeader
          headerTitle="IDM Support"
          field="idmSupport"
          type="singleSelect"
          onBulkEdit={handleBulkEdit}
          selectedRows={selectedRows}
          setSnack={setSnack}
          resetValue={headerResetTrigger}
        />
      ),
      cellClassName: (p) =>
        savedCells[p.id]?.[p.field]
          ? "saved"
          : editedCells[p.id]?.[p.field]
            ? "unsaved"
            : "",
      renderEditCell: (p) => (
        <Select
          fullWidth
          value={p.value || ""}
          onChange={(e) =>
            p.api.setEditCellValue({
              id: p.id,
              field: "idmSupport",
              value: e.target.value,
            })
          }
        >
          <MenuItem value="Out of Scope">Out of Scope</MenuItem>
          <MenuItem value="Full Support">Full Support</MenuItem>
          <MenuItem value="Other TTEC Access">Other TTEC Access</MenuItem>
          <MenuItem value="Phone IDs Only">Phone IDs Only</MenuItem>
        </Select>
      ),
    },

    // OG Supported
    {
      field: "ogSupported",
      headerName: "OG Supported",
      type: "boolean",
      hideable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      editable: true,
      headerAlign: "center",
      width: calculateColumnWidth(rows, "ogSupported", "OG Supported", 110),
      renderHeader: () => (
        <CustomBulkEditHeader
          headerTitle="OG Supported"
          field="ogSupported"
          type="boolean"
          onBulkEdit={handleBulkEdit}
          selectedRows={selectedRows}
          setSnack={setSnack}
          resetValue={headerResetTrigger}
        />
      ),
      cellClassName: (p) =>
        savedCells[p.id]?.[p.field]
          ? "saved"
          : editedCells[p.id]?.[p.field]
            ? "unsaved"
            : "",
      renderEditCell: (p) => (
        <Checkbox
          checked={p.value || false}
          onChange={(e) =>
            p.api.setEditCellValue({
              id: p.id,
              field: "ogSupported",
              value: e.target.checked,
            })
          }
        />
      ),
    },

    // IDM Deployment Status
    {
      field: "idmDeploymentStatus",
      headerName: "IDM Deployment Status",
      type: "singleSelect",
      hideable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      editable: true,
      headerAlign: "center",
      width: calculateColumnWidth(
        rows,
        "idmDeploymentStatus",
        "IDM Deployment Status",
        160,
      ),
      renderHeader: () => (
        <CustomBulkEditHeader
          headerTitle="IDM Deployment Status"
          field="idmDeploymentStatus"
          type="singleSelect"
          onBulkEdit={handleBulkEdit}
          selectedRows={selectedRows}
          setSnack={setSnack}
          resetValue={headerResetTrigger}
        />
      ),
      cellClassName: (p) =>
        savedCells[p.id]?.[p.field]
          ? "saved"
          : editedCells[p.id]?.[p.field]
            ? "unsaved"
            : "",
      renderEditCell: (p) => (
        <Select
          fullWidth
          value={p.value || ""}
          onChange={(e) =>
            p.api.setEditCellValue({
              id: p.id,
              field: "idmDeploymentStatus",
              value: e.target.value,
            })
          }
        >
          <MenuItem value="Complete">Complete</MenuItem>
          <MenuItem value="Ramp Down">Ramp Down</MenuItem>
          <MenuItem value="New Launch">New Launch</MenuItem>
          <MenuItem value="Out of Scope">Out of Scope</MenuItem>
        </Select>
      ),
    },

    // Ramp Down Date
    {
      field: "rampDownDate",
      headerName: "Ramp Down Date",
      type: "date",
      hideable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      editable: true,
      headerAlign: "center",
      width: calculateColumnWidth(rows, "rampDownDate", "Ramp Down Date", 130),
      renderHeader: () => (
        <CustomBulkEditHeader
          headerTitle="Ramp Down Date"
          field="rampDownDate"
          type="date"
          onBulkEdit={handleBulkEdit}
          selectedRows={selectedRows}
          setSnack={setSnack}
          resetValue={headerResetTrigger}
        />
      ),
      cellClassName: (p) =>
        savedCells[p.id]?.[p.field]
          ? "saved"
          : editedCells[p.id]?.[p.field]
            ? "unsaved"
            : "",
      renderEditCell: (p) => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            inputFormat="MM/DD/YYYY"
            renderInput={(props) => (
              <TextField {...props} fullWidth size="small" error={false} />
            )}
            value={moment(p.value)}
            onChange={(v) =>
              p.api.setEditCellValue({
                id: p.id,
                field: "rampDownDate",
                value: v ? format(new Date(v), "MM/dd/yyyy") : "",
              })
            }
          />
        </LocalizationProvider>
      ),
    },

    // rosterLockBusDays
    {
      field: "rosterLockBusDays",
      headerName: "Roster Lock",
      type: "number",
      hideable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      editable: true,
      headerAlign: "center",
      width: calculateColumnWidth(
        rows,
        "rosterLockBusDays",
        "Roster Lock",
        110,
      ),
      renderHeader: () => (
        <CustomBulkEditHeader
          headerTitle="Roster Lock"
          headerTooltip="Roster Lock (Business Days)"
          field="rosterLockBusDays"
          type="number"
          onBulkEdit={handleBulkEdit}
          selectedRows={selectedRows}
          setSnack={setSnack}
          resetValue={headerResetTrigger}
        />
      ),
      cellClassName: (p) =>
        savedCells[p.id]?.[p.field]
          ? "saved"
          : editedCells[p.id]?.[p.field]
            ? "unsaved"
            : "",
      renderEditCell: (p) => (
        <TextField
          fullWidth
          type="number"
          value={p.value ?? ""}
          onChange={(e) => {
            const v = Number(e.target.value.replace(/^0+(?!$)/, ""));
            if (!isNaN(v) && v >= 0 && v <= 99)
              p.api.setEditCellValue({
                id: p.id,
                field: "rosterLockBusDays",
                value: v,
              });
          }}
          inputProps={{ min: 0, max: 99, inputMode: "numeric" }}
        />
      ),
    },

    // rosterLockFrom
    {
      field: "rosterLockFrom",
      headerName: "Roster Lock From",
      type: "string",
      hideable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      editable: true,
      headerAlign: "center",
      width: calculateColumnWidth(
        rows,
        "rosterLockFrom",
        "Roster Lock From",
        130,
      ),
      renderHeader: () => (
        <CustomBulkEditHeader
          headerTitle="Roster Lock From"
          field="rosterLockFrom"
          type="string"
          onBulkEdit={handleBulkEdit}
          selectedRows={selectedRows}
          setSnack={setSnack}
          resetValue={headerResetTrigger}
        />
      ),
      cellClassName: (p) =>
        savedCells[p.id]?.[p.field]
          ? "saved"
          : editedCells[p.id]?.[p.field]
            ? "unsaved"
            : "",
      renderEditCell: (p) => (
        <TextField
          fullWidth
          value={p.value || ""}
          onChange={(e) =>
            p.api.setEditCellValue({
              id: p.id,
              field: "rosterLockFrom",
              value: e.target.value,
            })
          }
        />
      ),
    },

    // ttecAccessSLABusDays
    {
      field: "ttecAccessSLABusDays",
      headerName: "TTEC Access SLA",
      type: "number",
      hideable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      editable: true,
      headerAlign: "center",
      width: calculateColumnWidth(
        rows,
        "ttecAccessSLABusDays",
        "TTEC Access SLA",
        130,
      ),
      renderHeader: () => (
        <CustomBulkEditHeader
          headerTitle="TTEC Access SLA"
          headerTooltip="TTEC Access SLA (Business Days)"
          field="ttecAccessSLABusDays"
          type="number"
          onBulkEdit={handleBulkEdit}
          selectedRows={selectedRows}
          setSnack={setSnack}
          resetValue={headerResetTrigger}
        />
      ),
      cellClassName: (p) =>
        savedCells[p.id]?.[p.field]
          ? "saved"
          : editedCells[p.id]?.[p.field]
            ? "unsaved"
            : "",
      renderEditCell: (p) => (
        <TextField
          fullWidth
          type="number"
          value={p.value ?? ""}
          onChange={(e) => {
            const v = Number(e.target.value.replace(/^0+(?!$)/, ""));
            if (!isNaN(v) && v >= 0 && v <= 99)
              p.api.setEditCellValue({
                id: p.id,
                field: "ttecAccessSLABusDays",
                value: v,
              });
          }}
          inputProps={{ min: 0, max: 99, inputMode: "numeric" }}
        />
      ),
    },

    // clientAccessSLABusDays
    {
      field: "clientAccessSLABusDays",
      headerName: "Client Access SLA",
      type: "number",
      hideable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      editable: true,
      headerAlign: "center",
      width: calculateColumnWidth(
        rows,
        "clientAccessSLABusDays",
        "Client Access SLA",
        130,
      ),
      renderHeader: () => (
        <CustomBulkEditHeader
          headerTitle="Client Access SLA"
          headerTooltip="Client Access SLA (Business Days)"
          field="clientAccessSLABusDays"
          type="number"
          onBulkEdit={handleBulkEdit}
          selectedRows={selectedRows}
          setSnack={setSnack}
          resetValue={headerResetTrigger}
        />
      ),
      cellClassName: (p) =>
        savedCells[p.id]?.[p.field]
          ? "saved"
          : editedCells[p.id]?.[p.field]
            ? "unsaved"
            : "",
      renderEditCell: (p) => (
        <TextField
          fullWidth
          type="number"
          value={p.value ?? ""}
          onChange={(e) => {
            const v = Number(e.target.value.replace(/^0+(?!$)/, ""));
            if (!isNaN(v) && v >= 0 && v <= 99)
              p.api.setEditCellValue({
                id: p.id,
                field: "clientAccessSLABusDays",
                value: v,
              });
          }}
          inputProps={{ min: 0, max: 99, inputMode: "numeric" }}
        />
      ),
    },

    // String fields: clientAccessRequestType, primaryIDMPOC, backupIDMPOC, clientDistro,
    //                idmTeamLead, idmTeamEmail, primaryOGs, backupOGs, ogOwner,
    //                ogEscalationPoint, ogTeamEmail, idmOwner, idmEscalationPoint, switchIDPOC
    ...[
      { f: "clientAccessRequestType", label: "Client Access Request Type" },
      { f: "primaryIDMPOC", label: "Primary IDM POC" },
      { f: "backupIDMPOC", label: "Backup IDM POC" },
      { f: "clientDistro", label: "Client Distro" },
      { f: "idmTeamLead", label: "IDM Team Lead" },
      { f: "idmTeamEmail", label: "IDM Team Email" },
      { f: "primaryOGs", label: "Primary OG(s)" },
      { f: "backupOGs", label: "Backup OG(s)" },
      { f: "ogOwner", label: "OG Owner", base: 50 },
      { f: "ogEscalationPoint", label: "OG Escalation Point" },
      { f: "ogTeamEmail", label: "OG Team Email" },
      { f: "idmOwner", label: "IDM Owner" },
      { f: "idmEscalationPoint", label: "IDM Escalation Point" },
      { f: "switchIDPOC", label: "Switch ID POC", base: 20 },
    ].map(({ f, label, base = 0 }) => ({
      field: f,
      headerName: label,
      type: "string",
      hideable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      editable: true,
      headerAlign: "center",
      width: calculateColumnWidth(rows, f, label, 120),
      renderHeader: () => (
        <CustomBulkEditHeader
          headerTitle={label}
          field={f}
          type="string"
          onBulkEdit={handleBulkEdit}
          selectedRows={selectedRows}
          setSnack={setSnack}
          resetValue={headerResetTrigger}
        />
      ),
      cellClassName: (p) =>
        savedCells[p.id]?.[p.field]
          ? "saved"
          : editedCells[p.id]?.[p.field]
            ? "unsaved"
            : "",
      renderEditCell: (p) => (
        <TextField
          fullWidth
          value={p.value || ""}
          onChange={(e) =>
            p.api.setEditCellValue({
              id: p.id,
              field: f,
              value: e.target.value,
            })
          }
        />
      ),
    })),

    // clientOffboarding (boolean)
    {
      field: "clientOffboarding",
      headerName: "Client Offboarding",
      type: "boolean",
      hideable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      editable: true,
      headerAlign: "center",
      width: calculateColumnWidth(
        rows,
        "clientOffboarding",
        "Client Offboarding",
        140,
      ),
      renderHeader: () => (
        <CustomBulkEditHeader
          headerTitle="Client Offboarding"
          field="clientOffboarding"
          type="boolean"
          onBulkEdit={handleBulkEdit}
          selectedRows={selectedRows}
          setSnack={setSnack}
          resetValue={headerResetTrigger}
        />
      ),
      cellClassName: (p) =>
        savedCells[p.id]?.[p.field]
          ? "saved"
          : editedCells[p.id]?.[p.field]
            ? "unsaved"
            : "",
      renderEditCell: (p) => (
        <Checkbox
          checked={p.value || false}
          onChange={(e) =>
            p.api.setEditCellValue({
              id: p.id,
              field: "clientOffboarding",
              value: e.target.checked,
            })
          }
        />
      ),
    },

    // ── TA people-picker columns ───────────────────────────────────────────
    {
      field: "primaryTAName",
      headerName: "Primary TA",
      type: "string",
      hideable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      editable: true,
      headerAlign: "center",
      width: calculateColumnWidth(rows, "primaryTAName", "Primary TA", 150),
      renderHeader: () => (
        <CustomBulkEditHeader
          headerTitle="Primary TA"
          headerTooltip="Primary Training Administrator"
          field="primaryTAName"
          type="peoplepicker"
          onBulkEdit={handleBulkEditPeoplePicker}
          selectedRows={selectedRows}
          setSnack={setSnack}
          resetValue={headerResetTrigger}
        />
      ),
      cellClassName: (p) =>
        savedCells[p.id]?.primaryTAOracleId
          ? "saved"
          : editedCells[p.id]?.primaryTAOracleId
            ? "unsaved"
            : "",
      renderCell: (p) => (
        <Typography
          sx={{
            fontFamily: "Urbanist, Verdana, Arial, sans-serif",
            fontSize: "12px",
          }}
        >
          {p.value || ""}
        </Typography>
      ),
      renderEditCell: makePeoplePickerEditCell(
        "primaryTAName",
        "primaryTAOracleId",
      ),
    },
    {
      field: "backupTAName",
      headerName: "Backup TA",
      type: "string",
      hideable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      editable: true,
      headerAlign: "center",
      width: calculateColumnWidth(rows, "backupTAName", "Backup TA", 150),
      renderHeader: () => (
        <CustomBulkEditHeader
          headerTitle="Backup TA"
          headerTooltip="Backup Training Administrator"
          field="backupTAName"
          type="peoplepicker"
          onBulkEdit={handleBulkEditPeoplePicker}
          selectedRows={selectedRows}
          setSnack={setSnack}
          resetValue={headerResetTrigger}
        />
      ),
      cellClassName: (p) =>
        savedCells[p.id]?.backupTAOracleId
          ? "saved"
          : editedCells[p.id]?.backupTAOracleId
            ? "unsaved"
            : "",
      renderCell: (p) => (
        <Typography
          sx={{
            fontFamily: "Urbanist, Verdana, Arial, sans-serif",
            fontSize: "12px",
          }}
        >
          {p.value || ""}
        </Typography>
      ),
      renderEditCell: makePeoplePickerEditCell(
        "backupTAName",
        "backupTAOracleId",
      ),
    },
    {
      field: "taTeamEmail",
      headerName: "TA Team Email",
      type: "string",
      hideable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      editable: true,
      headerAlign: "center",
      width: calculateColumnWidth(rows, "taTeamEmail", "TA Team Email", 150),
      renderHeader: () => (
        <CustomBulkEditHeader
          headerTitle="TA Team Email"
          headerTooltip="Training Administrator Team Email"
          field="taTeamEmail"
          type="string"
          onBulkEdit={handleBulkEdit}
          selectedRows={selectedRows}
          setSnack={setSnack}
          resetValue={headerResetTrigger}
        />
      ),
      cellClassName: (p) =>
        savedCells[p.id]?.[p.field]
          ? "saved"
          : editedCells[p.id]?.[p.field]
            ? "unsaved"
            : "",
      renderCell: (p) => (
        <Typography
          sx={{
            fontFamily: "Urbanist, Verdana, Arial, sans-serif",
            fontSize: "12px",
          }}
        >
          {p.value || ""}
        </Typography>
      ),
      renderEditCell: (p) => (
        <TextField
          fullWidth
          value={p.value || ""}
          onChange={(e) =>
            p.api.setEditCellValue({
              id: p.id,
              field: "taTeamEmail",
              value: e.target.value,
            })
          }
        />
      ),
    },

    // ── L&D people-picker columns (generated from LD_PEOPLE_FIELDS) ────────
    ...LD_PEOPLE_FIELDS.map(({ field, oracleField, label }) => ({
      field,
      headerName: label,
      type: "string",
      hideable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      editable: true,
      headerAlign: "center",
      width: calculateColumnWidth(rows, field, label, 160),
      renderHeader: () => (
        <CustomBulkEditHeader
          headerTitle={label}
          headerTooltip={label}
          field={field}
          type="peoplepicker"
          onBulkEdit={handleBulkEditPeoplePicker}
          selectedRows={selectedRows}
          setSnack={setSnack}
          resetValue={headerResetTrigger}
        />
      ),
      cellClassName: (p) =>
        savedCells[p.id]?.[oracleField]
          ? "saved"
          : editedCells[p.id]?.[oracleField]
            ? "unsaved"
            : "",
      renderCell: (p) => (
        <Typography
          sx={{
            fontFamily: "Urbanist, Verdana, Arial, sans-serif",
            fontSize: "12px",
          }}
        >
          {p.value || ""}
        </Typography>
      ),
      renderEditCell: makePeoplePickerEditCell(field, oracleField),
    })),

    {
      field: "portfolioName",
      headerName: "Portfolio",
      type: "singleSelect",
      hideable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      editable: true,
      headerAlign: "center",
      width: calculateColumnWidth(rows, "portfolioName", "Portfolio", 130),
      renderHeader: () => (
        <CustomBulkEditHeader
          headerTitle="Portfolio"
          headerTooltip="Portfolio Assignment"
          field="portfolioName"
          type="singleSelect"
          valueOptions={portfolioOptions.map((p) => p.name)}
          onBulkEdit={handleBulkEditPortfolio} // receives ("portfolioName", "Healthcare")
          selectedRows={selectedRows}
          setSnack={setSnack}
          resetValue={headerResetTrigger}
        />
      ),
      cellClassName: (p) =>
        savedCells[p.id]?.lP_PortfolioId
          ? "saved"
          : editedCells[p.id]?.lP_PortfolioId
            ? "unsaved"
            : "",

      renderCell: (p) => (
        <Typography
          sx={{
            fontFamily: "Urbanist, Verdana, Arial, sans-serif",
            fontSize: "12px",
          }}
        >
          {p.value || "Healthcare"}
        </Typography>
      ),
      renderEditCell: (p) => (
        <Select
          fullWidth
          value={Number(p.row.lP_PortfolioId) || 4}
          disabled={portfolioLoading}
          onChange={(e) => {
            const id = Number(e.target.value);
            const name =
              portfolioOptions.find((o) => Number(o.id) === id)?.name || "";
            p.api.setEditCellValue({
              id: p.id,
              field: "portfolioName",
              value: name,
            });
            const row = rows.find((r) => r.id === p.id);
            if (row) row.lP_PortfolioId = id; // ← was lpPortfolioId
            markEdited([p.id], ["portfolioName", "lP_PortfolioId"]);
          }}
        >
          {portfolioOptions.map((o) => (
            <MenuItem key={o.id} value={Number(o.id)}>
              {o.name}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    // Boolean requirement columns (contactNumber … personalEmail) — unchanged
    ...[
      "contactNumber",
      "gender",
      "nationalIdentifier",
      "dateOfBirth",
      "country",
      "recruiter",
      "token",
      "confirmPreviousEmploymentWithClient",
      "nickname",
      "licenseNPN",
      "productTrainingDate",
      "address",
      "city",
      "state",
      "postalCode",
      "availability",
      "inboundOutbound",
      "language",
      "integrationDate",
      "email",
      "nestingDate",
      "productionDate",
      "backgroundStatusDate",
      "personalEmail",
    ].map((field) => {
      const labels = {
        contactNumber: "Contact Number",
        gender: "Gender",
        nationalIdentifier: "SSN / National Identifier",
        dateOfBirth: "Date of Birth",
        country: "Country",
        recruiter: "Recruiter",
        token: "Token",
        confirmPreviousEmploymentWithClient:
          "Confirm Previous Employment w/Client",
        nickname: "Nickname",
        licenseNPN: "License / NPN",
        productTrainingDate: "Product Training Date",
        address: "Address",
        city: "City",
        state: "State / Province",
        postalCode: "Postal Code",
        availability: "Availability",
        inboundOutbound: "Inbound / Outbound",
        language: "Language",
        integrationDate: "Integration Date",
        email: "Email",
        nestingDate: "Nesting Date",
        productionDate: "Production Date",
        backgroundStatusDate: "Background Status",
        personalEmail: "Personal Email",
      };
      return {
        field,
        headerName: labels[field],
        type: "boolean",
        hideable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        editable: true,
        headerAlign: "center",
        width: calculateColumnWidth(rows, field, labels[field], 120),
        renderHeader: () => (
          <CustomBulkEditHeader
            headerTitle={labels[field]}
            field={field}
            type="boolean"
            onBulkEdit={handleBulkEdit}
            selectedRows={selectedRows}
            setSnack={setSnack}
            resetValue={headerResetTrigger}
          />
        ),
        cellClassName: (p) =>
          savedCells[p.id]?.[p.field]
            ? "saved"
            : editedCells[p.id]?.[p.field]
              ? "unsaved"
              : "",
        renderEditCell: (p) => (
          <Checkbox
            checked={p.value || false}
            onChange={(e) =>
              p.api.setEditCellValue({
                id: p.id,
                field,
                value: e.target.checked,
              })
            }
          />
        ),
      };
    }),
  ];

  const handleCellFocusOut = (params) => {
    const fields = columns.map((c) => c.field);
    const colIdx = fields.indexOf(params.field);
    const rowIdx = rows.findIndex((r) => r.id === params.id);
    const lastCol = colIdx === fields.length - 1;
    const lastRow = rowIdx === rows.length - 1;
    if (shiftPressed) {
      if (colIdx > 0)
        document
          .querySelector(
            `[data-id="${params.id}"][data-field="${fields[colIdx - 1]}"]`,
          )
          ?.focus();
      else if (rowIdx > 0)
        document
          .querySelector(
            `[data-id="${rows[rowIdx - 1].id}"][data-field="${fields[fields.length - 1]}"]`,
          )
          ?.focus();
    } else if (lastCol) {
      if (!lastRow)
        document
          .querySelector(
            `[data-id="${rows[rowIdx + 1].id}"][data-field="${fields[0]}"]`,
          )
          ?.focus();
    } else {
      document
        .querySelector(
          `[data-id="${params.id}"][data-field="${fields[colIdx + 1]}"]`,
        )
        ?.focus();
    }
  };

  return (
    <Paper
      sx={{
        flexDirection: "column",
        boxShadow: "0px 5px 15px rgba(0,0,0,0.35)",
        padding: 3,
        overflow: "auto",
        height: "calc(100vh - 80px)",
        width: "calc(100vw - 125px)",
      }}
    >
      {loading && <BackdropComponent />}
      <Grid container spacing={2} item xs={12}>
        <Grid item xs={12}>
          <Typography
            sx={{
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              fontSize: 18,
              fontWeight: "bold",
              color: "#3F5765",
              letterSpacing: "0.10em",
            }}
          >
            Bulk Edit Account Ownership
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Box
            sx={{
              width: "100%",
              height: "calc(100vh - 225px)",
              overflow: "auto",
            }}
          >
            <StyledDataGrid
              rows={rows}
              columns={columns}
              disableColumnReorder
              disableVirtualization={false}
              onCellFocusOut={handleCellFocusOut}
              componentsProps={{ cell: { tabIndex: 0 } }}
              components={{
                Footer: () => (
                  <CustomFooter
                    pageSize={gridPageSize}
                    rowCount={filteredRowCount}
                    editableCellsCount={totalUnsavedCells || 0}
                  />
                ),
              }}
              pageSize={gridPageSize}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              checkboxSelection
              selectionModel={selectedRows}
              onSelectionModelChange={handleSelectionModelChange}
              onPageSizeChange={(n) => setGridPageSize(n)}
              processRowUpdate={handleProcessRowUpdate}
              experimentalFeatures={{ newEditingApi: true }}
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              width: "100%",
              marginTop: 3,
            }}
          >
            <CustomOutlinedButton
              variant="outlined"
              sx={{ minWidth: 85 }}
              onClick={handleFetchData}
            >
              Reset
            </CustomOutlinedButton>
            <CustomOutlinedButton
              variant="outlined"
              sx={{ minWidth: 85 }}
              onClick={handleCancelButton}
            >
              Cancel
            </CustomOutlinedButton>
            <CustomButton
              variant="contained"
              sx={{ minWidth: 85 }}
              onClick={handleSaveData}
            >
              Save
            </CustomButton>
          </Box>
        </Grid>
      </Grid>
      <DialogBox
        isOpen={dialogOpen}
        action={dialogAction}
        title={dialogTitle}
        message={dialogMessage}
        messageType={dialogMessageType || "Confirmation"}
        itemList={dialogBulletList}
        isConfirmation={isConfirmationDialog}
      />
      <SnackBar snack={snack} snackReset={snackReset} />
    </Paper>
  );
}

export default BulkEditIDMOGOwnerShip;
